'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback } from 'react';
import { Cookie, Shield, BarChart3, Megaphone, Share2, HelpCircle } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useCookieConsent } from '@/entities/cookie-consent';
import type { CookieCategory } from '@/entities/cookie-consent';

/**
 * Category metadata for display
 */
const CATEGORY_INFO: Record<CookieCategory, {
  label: string;
  description: string;
  icon: typeof Cookie;
  alwaysEnabled?: boolean;
}> = {
  essential: {
    label: 'Essential Cookies',
    description: 'Required for core functionality like authentication and security. Cannot be disabled.',
    icon: Shield,
    alwaysEnabled: true
  },
  performance: {
    label: 'Performance Cookies',
    description: 'Help us optimise site speed and performance by collecting anonymous usage data.',
    icon: BarChart3
  },
  analytics: {
    label: 'Analytics Cookies',
    description: 'Allow us to understand how you use Cova to improve our service. Powers session replay for debugging.',
    icon: BarChart3
  },
  advertising: {
    label: 'Advertising Cookies',
    description: 'Used to deliver relevant advertisements and measure campaign effectiveness.',
    icon: Megaphone
  },
  socialNetworking: {
    label: 'Social Networking Cookies',
    description: 'Enable sharing content on social media platforms and track social interactions.',
    icon: Share2
  },
  unclassified: {
    label: 'Unclassified Cookies',
    description: 'Cookies that have not yet been categorised. We review these regularly.',
    icon: HelpCircle
  }
};

/**
 * Order of categories in the modal
 */
const CATEGORY_ORDER: CookieCategory[] = ['essential', 'analytics', 'performance', 'advertising', 'socialNetworking', 'unclassified'];
interface CookiePreferencesModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Called after preferences are saved (optional) */
  onSave?: () => void;
}

/**
 * Cookie preferences modal with granular category selection
 *
 * Uses Sheet component sliding up from bottom for mobile-friendly UX.
 * Allows users to toggle individual cookie categories.
 *
 * @example
 * ```tsx
 * const [open, setOpen] = useState(false)
 *
 * return (
 *   <>
 *     <Button onClick={() => setOpen(true)}>Manage Preferences</Button>
 *     <CookiePreferencesModal
 *       open={open}
 *       onOpenChange={setOpen}
 *       onSave={() => console.log('Saved!')}
 *     />
 *   </>
 * )
 * ```
 */
export function CookiePreferencesModal(t0) {
  const $ = _c(43);
  const {
    open,
    onOpenChange,
    onSave
  } = t0;
  const {
    categories,
    acceptAll,
    rejectAll,
    acceptCategories
  } = useCookieConsent();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      essential: true,
      performance: false,
      analytics: false,
      advertising: false,
      socialNetworking: false,
      unclassified: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [pendingCategories, setPendingCategories] = useState(t1);
  const [prevOpen, setPrevOpen] = useState(false);
  const [prevCategories, setPrevCategories] = useState(categories);
  if (open !== prevOpen || categories !== prevCategories) {
    setPrevOpen(open);
    setPrevCategories(categories);
    if (open) {
      setPendingCategories({
        essential: true,
        performance: categories.performance,
        analytics: categories.analytics,
        advertising: categories.advertising,
        socialNetworking: categories.socialNetworking,
        unclassified: categories.unclassified
      });
    }
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (category, enabled) => {
      if (category === "essential") {
        return;
      }
      setPendingCategories(prev => ({
        ...prev,
        [category]: enabled
      }));
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleToggle = t2;
  let t3;
  if ($[2] !== acceptAll || $[3] !== onOpenChange || $[4] !== onSave) {
    t3 = () => {
      acceptAll();
      onOpenChange(false);
      onSave?.();
    };
    $[2] = acceptAll;
    $[3] = onOpenChange;
    $[4] = onSave;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleAcceptAll = t3;
  let t4;
  if ($[6] !== onOpenChange || $[7] !== onSave || $[8] !== rejectAll) {
    t4 = () => {
      rejectAll();
      onOpenChange(false);
      onSave?.();
    };
    $[6] = onOpenChange;
    $[7] = onSave;
    $[8] = rejectAll;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleRejectAll = t4;
  let t5;
  if ($[10] !== acceptCategories || $[11] !== onOpenChange || $[12] !== onSave || $[13] !== pendingCategories.advertising || $[14] !== pendingCategories.analytics || $[15] !== pendingCategories.performance || $[16] !== pendingCategories.socialNetworking || $[17] !== pendingCategories.unclassified) {
    t5 = () => {
      acceptCategories({
        performance: pendingCategories.performance,
        analytics: pendingCategories.analytics,
        advertising: pendingCategories.advertising,
        socialNetworking: pendingCategories.socialNetworking,
        unclassified: pendingCategories.unclassified
      });
      onOpenChange(false);
      onSave?.();
    };
    $[10] = acceptCategories;
    $[11] = onOpenChange;
    $[12] = onSave;
    $[13] = pendingCategories.advertising;
    $[14] = pendingCategories.analytics;
    $[15] = pendingCategories.performance;
    $[16] = pendingCategories.socialNetworking;
    $[17] = pendingCategories.unclassified;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const handleSavePreferences = t5;
  let t6;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SheetHeader className="pb-4"><div className="flex items-center gap-2"><div className="shrink-0 w-8 h-8 rounded-full bg-[#6252fd]/10 flex items-center justify-center"><Cookie className="h-4 w-4 text-[#6252fd]" /></div><SheetTitle className="text-lg">Cookie Preferences</SheetTitle></div><SheetDescription id="cookie-preferences-description">Manage your cookie preferences. Essential cookies are always enabled for core functionality.</SheetDescription></SheetHeader>;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== pendingCategories) {
    t7 = CATEGORY_ORDER.map((category_0, index) => {
      const info = CATEGORY_INFO[category_0];
      const Icon = info.icon;
      const isEnabled = info.alwaysEnabled || pendingCategories[category_0];
      return <div key={category_0}>{index > 0 && <Separator className="my-4" />}<div className="flex items-start justify-between gap-4 py-3"><div className="flex items-start gap-3 flex-1 min-w-0"><div className="shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mt-0.5"><Icon className="h-4 w-4 text-gray-600" /></div><div className="flex-1 min-w-0"><Label className="text-sm font-medium text-gray-900">{info.label}</Label><p className="text-sm text-gray-500 mt-1 leading-relaxed">{info.description}</p></div></div><Switch checked={isEnabled} onCheckedChange={checked => handleToggle(category_0, checked)} disabled={info.alwaysEnabled} className={info.alwaysEnabled ? "data-[state=checked]:bg-green-500 cursor-not-allowed" : "data-[state=checked]:bg-[#6252fd]"} aria-label={`${info.alwaysEnabled ? "Always enabled" : isEnabled ? "Disable" : "Enable"} ${info.label}`} data-testid={`cookie-prefs-modal-${category_0}-toggle`} /></div></div>;
    });
    $[20] = pendingCategories;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t7) {
    t8 = <div className="overflow-y-auto max-h-[50vh] px-4"><div className="space-y-2">{t7}</div></div>;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== handleRejectAll) {
    t9 = <Button variant="outline" onClick={handleRejectAll} className="flex-1 sm:flex-none border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="cookie-prefs-modal-reject-all">Reject All</Button>;
    $[24] = handleRejectAll;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== handleAcceptAll) {
    t10 = <Button variant="outline" onClick={handleAcceptAll} className="flex-1 sm:flex-none border-[#6252fd] text-[#6252fd] hover:bg-[#6252fd]/5" data-testid="cookie-prefs-modal-accept-all">Accept All</Button>;
    $[26] = handleAcceptAll;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t9) {
    t11 = <div className="flex gap-2 order-2 sm:order-1">{t9}{t10}</div>;
    $[28] = t10;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== handleSavePreferences) {
    t12 = <Button onClick={handleSavePreferences} className="order-1 sm:order-2 bg-[#6252fd] hover:bg-[#5242ed] text-white" data-testid="cookie-prefs-modal-save">Save Preferences</Button>;
    $[31] = handleSavePreferences;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t11 || $[34] !== t12) {
    t13 = <SheetFooter className="pt-4 flex flex-col gap-2 sm:flex-row sm:justify-between">{t11}{t12}</SheetFooter>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t13 || $[37] !== t8) {
    t14 = <SheetContent side="bottom" className="h-auto max-h-[85vh] rounded-t-xl" aria-describedby="cookie-preferences-description" data-testid="cookie-prefs-modal">{t6}{t8}{t13}</SheetContent>;
    $[36] = t13;
    $[37] = t8;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== onOpenChange || $[40] !== open || $[41] !== t14) {
    t15 = <Sheet open={open} onOpenChange={onOpenChange}>{t14}</Sheet>;
    $[39] = onOpenChange;
    $[40] = open;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  return t15;
}